/**
 * @description
 * Stripe usage service for managing usage tracking, pricing, and analytics.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type { 
  PricingConfig, 
  UsageAccumulator, 
  UsageData, 
  BillingStats 
} from "./types";

/**
 * Stripe usage service
 */
export class StripeUsageService extends BaseAPIClient {
  /**
   * Get pricing configuration for client
   */
  async getPricingConfig(clientId: string): Promise<PricingConfig> {
    const data = await this.request<{ config: PricingConfig }>(`/stripe/pricing/${clientId}`);
    return data.config;
  }

  /**
   * Update pricing configuration for client
   */
  async updatePricingConfig(clientId: string, config: Partial<{
    billingModel: 'subscription' | 'usage' | 'hybrid';
    marginPercentage: number;
    flatFee: number;
    usageThreshold: number;
    thresholdAlertAt: number;
    autoChargeOnThreshold: boolean;
  }>): Promise<any> {
    const response = await this.request<{ config: PricingConfig }>(`/stripe/pricing/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
    return response.config;
  }

  /**
   * Get usage accumulator status for client
   */
  async getUsageAccumulator(clientId: string): Promise<UsageAccumulator> {
    const data = await this.request<{ status: UsageAccumulator }>(`/stripe/usage-accumulator/${clientId}`);
    return data.status;
  }

  /**
   * Get usage data for a client
   */
  async getUsageData(clientId: string, timeRange: '7d' | '30d' | '90d'): Promise<UsageData[]> {
    const response = await this.request<{ usageData: UsageData[] }>(`/stripe/usage-data/${clientId}?timeRange=${timeRange}`);
    return response.usageData;
  }

  /**
   * Sync Anthropic costs
   */
  async syncAnthropicCosts(clientId?: string): Promise<{ synced: number; message: string }> {
    const endpoint = clientId ? `/stripe/anthropic-costs/sync?client_id=${clientId}` : '/stripe/anthropic-costs/sync';
    return this.request<{ synced: number; message: string }>(endpoint, {
      method: 'POST',
    });
  }

  /**
   * Check thresholds manually
   */
  async checkThresholds(): Promise<{
    results: Array<{
      clientId: string;
      currentAmount: number;
      threshold: number;
      percentUsed: number;
      shouldInvoice: boolean;
      shouldAlert: boolean;
    }>;
    summary: {
      total: number;
      shouldInvoice: number;
      shouldAlert: number;
    };
  }> {
    return this.request<{
      results: Array<{
        clientId: string;
        currentAmount: number;
        threshold: number;
        percentUsed: number;
        shouldInvoice: boolean;
        shouldAlert: boolean;
      }>;
      summary: {
        total: number;
        shouldInvoice: number;
        shouldAlert: number;
      };
    }>('/stripe/invoices/check-thresholds', {
      method: 'POST',
    });
  }

  /**
   * Get billing statistics
   */
  async getBillingStats(): Promise<BillingStats> {
    const response = await this.request<BillingStats>('/stripe/billing-stats');
    return response;
  }
}

// Export singleton instance
export const stripeUsageService = new StripeUsageService();
